import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface CheerButtonProps {
  activityId: string;
  className?: string;
}

export function CheerButton({ activityId, className }: CheerButtonProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cheerCount, setCheerCount] = useState(0);
  const [hasCheered, setHasCheered] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    loadCheers();
  }, [user, activityId]);

  const loadCheers = async () => {
    try {
      // Get total cheer count
      const { count } = await supabase
        .from('activity_reactions')
        .select('*', { count: 'exact', head: true })
        .eq('activity_id', activityId);

      setCheerCount(count || 0);

      // Check if current user has cheered
      const { data } = await supabase
        .from('activity_reactions')
        .select('id')
        .eq('activity_id', activityId)
        .eq('user_id', user!.id)
        .maybeSingle();

      setHasCheered(!!data);
    } catch (error) {
      console.error('Error loading cheers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheer = async () => {
    if (!user || loading) return;

    try {
      setLoading(true);

      if (hasCheered) {
        // Remove cheer
        const { error } = await supabase
          .from('activity_reactions')
          .delete()
          .eq('activity_id', activityId)
          .eq('user_id', user.id);

        if (error) throw error;

        setCheerCount(prev => Math.max(0, prev - 1));
        setHasCheered(false);
      } else {
        // Add cheer
        const { error } = await supabase
          .from('activity_reactions')
          .insert({ 
            activity_id: activityId, 
            user_id: user.id, 
            reaction_type: 'cheer' 
          });

        if (error) throw error;

        setCheerCount(prev => prev + 1);
        setHasCheered(true);

        // Award XP for cheering
        await supabase.rpc('award_xp', {
          p_user_id: user.id,
          p_amount: 2,
          p_reason: 'Cheered on friend activity',
          p_activity_type: 'cheer',
          p_related_activity_id: activityId
        });
      }
    } catch (error) {
      console.error('Cheer error:', error);
      toast({ 
        variant: 'destructive', 
        title: 'Error', 
        description: 'Could not update cheer' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCheer}
      disabled={loading}
      className={`group touch-target tap-feedback gap-1.5 px-3 ${hasCheered ? 'text-destructive' : 'text-muted-foreground'} ${className}`}
    >
      <Heart 
        className={`w-5 h-5 transition-all duration-200 ${
          hasCheered 
            ? 'fill-destructive text-destructive scale-110' 
            : 'group-hover:text-destructive/70 group-active:scale-125'
        }`} 
      />
      {cheerCount > 0 && (
        <span className="text-sm font-medium tabular-nums">{cheerCount}</span>
      )}
    </Button>
  );
}
